import { useMemo } from 'react';
import { ObjectType } from 'api/pushes';
import type { FilterableField } from 'api/tasks/filterable-field';
import { PhaseEntity } from 'components/organisms/projects/project-plan/phase-entity/generics';
import { getId } from 'components/organisms/projects/project-plan/phase-entity/utils';
import { SheetTab } from 'components/pages/templates/newTemplateData';
import { PhaseEntityType, converToEntityId } from 'data/projects/phase-entity';
import { useSheetsFeature } from 'hooks/use-sheets-feature';
import { getFieldMapReducer } from 'routes/projects/list-v2/transformer';
import { floatUid } from 'utils/uid';
import {
  ProjectTemplateData,
  ProjectTemplateProjectPhase,
  ProjectTemplateSprint,
  ProjectTemplateTask,
} from '../types';
import { useGetAllProjectPhases } from './use-get-all-project-phases';
import { useGetAllProjectPhasesInTemplate } from './use-get-all-project-phases-in-template';
import { useGetAllSprints } from './use-get-all-sprints';
import { useGetAllTasks } from './use-get-all-tasks';

type FieldMapType = (
  hash: Record<string, unknown>,
  { fieldValue, fieldId }: { fieldId: number; fieldValue: unknown },
) => Record<string, unknown>;

const frameTaskObject = (task: ProjectTemplateTask, rootIndex: number) => ({
  key: `TASK-${task.taskId}`,
  id: task.taskId,
  title: task.taskName,
  startDay: task.startDay,
  duration: task.duration,
  assignee: task.assignee,
  private: task.private,
  data: task,
  rootIndex,
});

const frameSheetTabObject = (
  task: SheetTab,
  rootIndex: number,
  taskId: number,
) => ({
  key: `SHEET-TAB-${taskId}_${task.sheetTabId}`,
  id: `${taskId}_${task.sheetTabId}`,
  title: task.sheetTabName,
  startDay: 1,
  duration: 1,
  assignee: task.assignee,
  private: false,
  data: task,
  rootIndex,
  taskId,
});

const frameSprintObject = (
  sprint: ProjectTemplateSprint,
  rootIndex: number,
) => ({
  key: `SPRINT-${sprint.sprintId}`,
  id: sprint.sprintId,
  title: sprint.sprintName,
  startDay: sprint.startDay,
  duration: sprint.duration,
  private: sprint.private,
  data: sprint,
  rootIndex,
});

export const frameTaskObjectForBulkUpdate = (
  task: ProjectTemplateTask,
  rootIndex: number,
  toFieldMap: FieldMapType,
) => ({
  ...(task.fields ?? []).reduce(toFieldMap, {}),
  ...task,
  key: `TASK-${task.taskId}`,
  id: task.taskId,
  title: task.taskName,
  startDay: task.startDay,
  duration: task.duration,
  assignee: task.assignee,
  private: task.private,
  data: {
    ...task,
    defaultTimeEntryBillable: task.defaultTimeEntryBillable ?? true,
  },
  defaultTimeEntryBillable: task.defaultTimeEntryBillable ?? true,
  rootIndex,
});

const noop = () => ({});

const getEntityFromRef = <T extends PhaseEntity>(
  entity: T | number,
  lookup: { [key: string]: T },
) => {
  if (typeof entity === 'number') {
    return lookup[entity];
  }

  return entity;
};

const getEntityIdFromRef = <T extends PhaseEntity>(
  entity: T | number,
  entityType: PhaseEntityType,
) => {
  if (typeof entity === 'number') {
    return converToEntityId(entity, entityType);
  }

  return getId(entity, true);
};

type ConverterType =
  | typeof frameTaskObject
  | typeof frameTaskObjectForBulkUpdate;

const taskToChild = (
  taskRef: ProjectTemplateTask,
  {
    phaseIndex: index,
    allTasks,
    projectPhase,
  }: {
    phaseIndex: number;
    allTasks: {
      [key: string]: ProjectTemplateTask;
    };
    projectPhase: ProjectTemplateProjectPhase;
  },
) => {
  const task = getEntityFromRef(taskRef, allTasks);

  // `getEntityFromRef` can return undefined if a phase still references a
  // task ID that was deleted/renumbered (stale lookup). Without this guard,
  // `frameTaskObject(undefined)` accesses `task.taskId` and crashes the
  // template plan view with "Cannot read properties of undefined (reading
  // 'taskId')".
  if (!task) {
    return null;
  }

  return {
    ...frameTaskObject(task, index),
    type: 'TASK',
    level: 1,
    parentTotal: projectPhase.tasks.length,
    children: task.subTasks
      ?.map((subTaskRef) => {
        const subTask = getEntityFromRef(subTaskRef, allTasks);

        if (!subTask) {
          return null;
        }

        return {
          ...frameTaskObject(subTask, index),
          type: 'TASK',
          level: 2,
          parentTotal: task.subTasks.length,
          children: subTask.subTasks
            ?.map((_subTaskRef) => {
              const _subTask = getEntityFromRef(_subTaskRef, allTasks);

              if (!_subTask) {
                return null;
              }

              return {
                ...frameTaskObject(_subTask, index),
                parentTotal: subTask.subTasks.length,
                level: 3,
                type: 'TASK',
                children: [],
              };
            })
            .filter(Boolean),
        };
      })
      .filter(Boolean),
  };
};

const taskToRow = (
  taskRef: ProjectTemplateTask,
  {
    phaseIndex: index,
    allTasks,
    projectPhase,
    result,
    converter,
    toFieldMap,
    isSheetsEnabled = true,
  }: {
    phaseIndex: number;
    allTasks: {
      [key: string]: ProjectTemplateTask;
    };
    projectPhase: ProjectTemplateProjectPhase;
    result: any[];
    converter: ConverterType;
    toFieldMap: FieldMapType;
    isSheetsEnabled?: boolean;
  },
) => {
  const task = getEntityFromRef(taskRef, allTasks);

  // Same stale-lookup guard as `taskToChild` — when a phase references a
  // task ID that's no longer in `allTasks`, skip the row rather than
  // crashing on `task.taskId`.
  if (!task) {
    return;
  }

  if (!isSheetsEnabled && task?.sheetTemplate?.templateId) {
    return;
  }

  let phaseTask = {};

  // Sheet Template Case
  if (task?.sheetTemplate) {
    // Reuse the same converter as normal tasks so the row keeps the
    // converter-specific shape (e.g. `frameTaskObjectForBulkUpdate`
    // spreads `...task` at the top level so AG Grid can read fields like
    // `effort` via `field: 'effort'`). Only the sheet-specific bits
    // (`SHEET-` key prefix and `taskId` accessor) need to be overlaid.
    phaseTask = {
      ...converter(task, index, toFieldMap),
      key: `SHEET-${task.taskId}`,
      taskId: task.taskId,
      type: 'TASK',
      level: 1,
      parentTotal: projectPhase.tasks.length,
      hierarchy: [projectPhase.projectPhaseId, task.taskId].map(String),
    };

    // Add Sheet fprojectPhase.projectPhaseIdTab as subtask.
    task?.sheetTemplate?.sheetTabs?.forEach((tab) => {
      result.push({
        ...frameSheetTabObject(tab, index, task.taskId),
        type: 'SHEET_TAB',
        level: 2,
        parentTotal: task?.sheetTemplate?.sheetTabs?.length,
        hierarchy: [
          projectPhase.projectPhaseId,
          task.taskId,
          `${task.taskId}_${tab.sheetTabId}`,
        ].map(String),
      });
    });
  } else {
    phaseTask = {
      ...converter(task, index, toFieldMap),
      type: 'TASK',
      level: 1,
      parentTotal: projectPhase.tasks.length,
      hierarchy: [projectPhase.projectPhaseId, task.taskId].map(String),
    };
  }

  result.push(phaseTask);

  task.subTasks?.forEach((subTaskRef) => {
    const subTask = getEntityFromRef(subTaskRef, allTasks);

    if (!subTask) {
      return;
    }

    const taskSubTask = {
      ...converter(subTask, index, toFieldMap),
      type: 'TASK',
      level: 2,
      parentTotal: task.subTasks.length,
      hierarchy: [projectPhase.projectPhaseId, task.taskId, subTask.taskId].map(
        String,
      ),
    };

    result.push(taskSubTask);

    subTask.subTasks?.forEach((_subTaskRef) => {
      const _subTask = getEntityFromRef(_subTaskRef, allTasks);

      if (!_subTask) {
        return;
      }

      const secondLevelSubTask = {
        ...converter(_subTask, index, toFieldMap),
        parentTotal: subTask.subTasks.length,
        level: 3,
        type: 'TASK',
        hierarchy: [
          projectPhase.projectPhaseId,
          task.taskId,
          subTask.taskId,
          _subTask.taskId,
        ].map(String),
      };

      result.push(secondLevelSubTask);
    });
  });
};

const getPhaseSprint = ({
  sprint,
  projectPhase,
  index,
}: {
  sprint: ProjectTemplateSprint;
  projectPhase: ProjectTemplateProjectPhase;
  index: number;
}): ProjectTemplateData => ({
  ...frameSprintObject(sprint ?? {}, index),
  type: ObjectType.Sprint,
  level: 1,
  parentTotal: projectPhase.sprints?.length,
  hierarchy: [projectPhase.projectPhaseId, sprint.sprintId].map(String),
});

const addSprintEntity = ({
  projectPhase,
  allSprints,
  index,
  entitiesMap,
  isAgGrid,
}: {
  projectPhase: ProjectTemplateProjectPhase;
  allSprints: { [key: string]: ProjectTemplateSprint };
  entitiesMap: Record<string, ProjectTemplateData | ProjectTemplateData[]>;
  index: number;
  isAgGrid?: boolean;
}) => {
  projectPhase.sprints?.forEach((sprintRef) => {
    const key = getEntityIdFromRef(sprintRef, PhaseEntityType.Sprint);

    const sprint = getEntityFromRef(sprintRef, allSprints);

    const phaseSprint = getPhaseSprint({
      sprint: sprint ?? {},
      projectPhase,
      index,
    });

    entitiesMap[key] = isAgGrid ? [phaseSprint] : phaseSprint;
  });
};

export const useTransformTabularData = (
  phases?: ProjectTemplateProjectPhase[] | null,
): ProjectTemplateData[] => {
  const projectPhaseIds = useGetAllProjectPhasesInTemplate();
  const projectPhases = useGetAllProjectPhases();
  const allTasks = useGetAllTasks();
  const allSprints = useGetAllSprints();

  return useMemo(() => {
    const _phases =
      phases !== undefined && phases !== null
        ? phases || []
        : (projectPhaseIds ?? []).map((id) => projectPhases[id]);

    return _phases.map((projectPhase, index) => {
      const entitiesMap = {};
      const order = projectPhase.entityOrder ?? [];

      projectPhase.tasks.forEach((taskRef) => {
        const key = getEntityIdFromRef(taskRef, PhaseEntityType.Task);

        if (!key) {
          return;
        }

        entitiesMap[key] = taskToChild(taskRef, {
          phaseIndex: index,
          allTasks,
          projectPhase,
        });
      });

      addSprintEntity({
        projectPhase,
        allSprints,
        entitiesMap,
        index,
      });

      const entities = order.map((id) => entitiesMap[id]).filter(Boolean);

      return {
        key: `PROJECT_PHASE-${projectPhase.projectPhaseId}`,
        id: projectPhase.projectPhaseId,
        title: projectPhase.projectPhaseName,
        startDay: projectPhase.startDay,
        duration: projectPhase.duration,
        type: 'PROJECT_PHASE',
        private: projectPhase.private,
        level: 0,
        data: projectPhase,
        rootIndex: index,
        parentTotal: 0,
        children: entities,
      };
    });
  }, [projectPhaseIds, projectPhases, phases, allTasks, allSprints]);
};

export const useTransformTabularDataForAgGrid = (
  phases?: ProjectTemplateProjectPhase[],
): ProjectTemplateData[] => {
  const projectPhaseIds = useGetAllProjectPhasesInTemplate();
  const projectPhases = useGetAllProjectPhases();
  const allTasks = useGetAllTasks();
  const allSprints = useGetAllSprints();
  const isSheetsEnabled = useSheetsFeature();

  return useMemo(() => {
    const _phases =
      phases !== undefined && phases !== null
        ? phases || []
        : (projectPhaseIds ?? []).map((id) => projectPhases[id]);

    const result: any[] = [];

    _phases.forEach((projectPhase, index) => {
      const phase = {
        key: `PROJECT_PHASE-${projectPhase.projectPhaseId}`,
        id: projectPhase.projectPhaseId,
        title: projectPhase.projectPhaseName,
        startDay: projectPhase.startDay,
        duration: projectPhase.duration,
        type: 'PROJECT_PHASE',
        private: projectPhase.private,
        level: 0,
        data: projectPhase,
        rootIndex: index,
        parentTotal: 0,
        hierarchy: [projectPhase.projectPhaseId].map(String),
      };

      result.push(phase);
      const entitiesMap = {};
      const order = projectPhase.entityOrder ?? [];

      projectPhase.tasks.forEach((taskRef) => {
        const taskEntity: any[] = [];
        const key = getEntityIdFromRef(taskRef, PhaseEntityType.Task);

        if (!key) {
          return;
        }

        taskToRow(taskRef, {
          phaseIndex: index,
          allTasks,
          projectPhase,
          result: taskEntity,
          converter: frameTaskObject,
          toFieldMap: noop,
          isSheetsEnabled,
        });

        entitiesMap[key] = taskEntity;
      });

      addSprintEntity({
        projectPhase,
        allSprints,
        entitiesMap,
        index,
        isAgGrid: true,
      });

      const entities = order.map((id) => entitiesMap[id]).filter(Boolean);

      result.push(...entities.flat());
    });

    const newPhaseId = floatUid();
    const hasNewPhaseRow = _phases?.[_phases?.length - 1]?.isNew;

    if (hasNewPhaseRow) {
      return result;
    }
    result.push({
      key: `PROJECT_PHASE-${newPhaseId}`,
      id: newPhaseId,
      type: 'PROJECT_PHASE',
      level: 0,
      data: {
        isNew: true,
        entityOrder: [],
      },
      rootIndex: _phases.length,
      hierarchy: [`${newPhaseId}`],
      insertAt: null,
    });

    return result;
  }, [
    projectPhaseIds,
    projectPhases,
    allTasks,
    phases,
    allSprints,
    isSheetsEnabled,
  ]);
};

export const useTransformTabularDataForBulkTaskGrid = (
  lookup: Record<number, FilterableField>,
): ProjectTemplateData[] => {
  const projectPhaseIds = useGetAllProjectPhasesInTemplate();
  const projectPhases = useGetAllProjectPhases();
  const allTasks = useGetAllTasks();
  const allSprints = useGetAllSprints();
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  const toFieldMap = useMemo(() => getFieldMapReducer(lookup), [lookup]);
  const isSheetsEnabled = useSheetsFeature();

  return useMemo(() => {
    const _phases = (projectPhaseIds ?? []).map((id) => projectPhases[id]);
    const result: any[] = [];

    _phases.forEach((projectPhase, index) => {
      const phase = {
        key: `PROJECT_PHASE-${projectPhase.projectPhaseId}`,
        id: projectPhase.projectPhaseId,
        title: projectPhase.projectPhaseName,
        startDay: projectPhase.startDay,
        duration: projectPhase.duration,
        type: 'PROJECT_PHASE',
        private: projectPhase.private,
        level: 0,
        data: projectPhase,
        rootIndex: index,
        parentTotal: 0,
        hierarchy: [projectPhase.projectPhaseId].map(String),
      };

      result.push(phase);

      const entitiesMap = {};
      const order = projectPhase.entityOrder ?? [];

      projectPhase.tasks.forEach((taskRef) => {
        const taskEntity: any[] = [];
        const key = getEntityIdFromRef(taskRef, PhaseEntityType.Task);

        if (!key) {
          return;
        }

        taskToRow(taskRef, {
          phaseIndex: index,
          allTasks,
          projectPhase,
          result: taskEntity,
          converter: frameTaskObjectForBulkUpdate,
          toFieldMap,
          isSheetsEnabled,
        });

        entitiesMap[key] = taskEntity;
      });

      addSprintEntity({
        projectPhase,
        allSprints,
        entitiesMap,
        index,
        isAgGrid: true,
      });

      const entities = order.map((id) => entitiesMap[id]).filter(Boolean);

      result.push(...entities.flat());
    });

    const hasNewPhaseRow = _phases?.[_phases?.length - 1]?.isNew;

    if (hasNewPhaseRow) {
      return result;
    }
    const newPhaseId = floatUid();

    result.push({
      key: `PROJECT_PHASE-${newPhaseId}`,
      id: newPhaseId,
      type: 'PROJECT_PHASE',
      level: 0,
      data: {
        isNew: true,
        entityOrder: [],
      },
      rootIndex: _phases.length,
      hierarchy: [`${newPhaseId}`],
      insertAt: null,
    });

    return result;
  }, [
    projectPhaseIds,
    projectPhases,
    allTasks,
    toFieldMap,
    allSprints,
    isSheetsEnabled,
  ]);
};
